/**
 * Mega Menu Controller
 * Handles 3-level navigation menu with desktop (≥1024px) and mobile behaviors
 */

class MegaMenuController {
  constructor(menuDrawerId) {
    this.menuDrawer = document.getElementById(menuDrawerId);
    if (!this.menuDrawer) return;

    this.mainPanel = this.menuDrawer.querySelector('#menu-drawer-panel-main');
    if (!this.mainPanel) return;

    this.init();
  }

  /**
   * Promo images are wrapped in <template data-mega-menu-promo> in fd-mega-menu.liquid so they
   * are excluded from the live DOM tree (and its style/layout cost) until the drawer is actually
   * opened. Clone them into place once, right before the drawer becomes visible.
   */
  hydratePromoImages() {
    this.menuDrawer.querySelectorAll('template[data-mega-menu-promo]').forEach((template) => {
      template.replaceWith(template.content.cloneNode(true));
    });
  }

  /**
   * Check if we're in desktop mode (≥1024px)
   */
  isDesktop() {
    return window.matchMedia('(min-width: 1024px)').matches;
  }

  /**
   * Ensure main panel stays visible in desktop when there are active secondary/tertiary panels
   */
  ensureMainPanelVisible() {
    if (this.isDesktop() && this.mainPanel && !this.mainPanel.classList.contains('is-visible')) {
      this.mainPanel.classList.add('is-visible');
    }
  }

  /**
   * Close sibling panels at the same level
   */
  closeSiblingPanels(targetPanelId) {
    // Determine the level of the panel being opened
    const isLevel2 = targetPanelId.match(/^menu-drawer-panel-\d+$/);
    const isLevel3 = targetPanelId.match(/^menu-drawer-panel-\d+-\d+$/);

    if (isLevel2) {
      // Opening level 2: close all other level 2 panels and all level 3 panels
      this.menuDrawer.querySelectorAll('.menu-drawer__panel--secondary.is-visible').forEach((panel) => {
        if (panel.id !== targetPanelId) {
          panel.classList.remove('is-visible');
        }
      });
      // Close all level 3 panels
      this.menuDrawer.querySelectorAll('.menu-drawer__panel--tertiary.is-visible').forEach((panel) => {
        panel.classList.remove('is-visible');
      });
    } else if (isLevel3) {
      // Opening level 3: close all other level 3 panels (siblings)
      this.menuDrawer.querySelectorAll('.menu-drawer__panel--tertiary.is-visible').forEach((panel) => {
        if (panel.id !== targetPanelId) {
          panel.classList.remove('is-visible');
        }
      });
    }
  }

  /**
   * Handle panel toggle button clicks
   */
  handlePanelToggle(e) {
    const toggleButton = e.target.closest('[data-panel-toggle]');
    if (!toggleButton) return;

    const targetPanelId = toggleButton.getAttribute('aria-controls');
    if (!targetPanelId) return;

    // Close sibling panels before the native system opens the new panel
    this.closeSiblingPanels(targetPanelId);

    // Ensure main panel stays visible in desktop after any click
    setTimeout(() => this.ensureMainPanelVisible(), 50);
  }

  /**
   * Update button active state based on panel visibility
   */
  updateButtonState(panel) {
    const panelId = panel.id;

    // Skip the main panel
    if (panelId === 'menu-drawer-panel-main') return;

    // Find the corresponding toggle button
    const toggleButton = this.menuDrawer.querySelector(`[data-panel-id="${panelId}"]`);
    if (!toggleButton) return;

    // Add or remove is-active class based on panel visibility
    if (panel.classList.contains('is-visible')) {
      toggleButton.classList.add('is-active');
    } else {
      toggleButton.classList.remove('is-active');
    }
  }

  /**
   * Prevent main panel from being hidden in desktop when there are active panels
   */
  preventMainPanelHide() {
    if (this.isDesktop() && !this.mainPanel.classList.contains('is-visible')) {
      // Check if there are any secondary or tertiary panels visible
      const hasVisiblePanels = this.menuDrawer.querySelector(
        '.menu-drawer__panel--secondary.is-visible, .menu-drawer__panel--tertiary.is-visible'
      );
      if (hasVisiblePanels) {
        this.mainPanel.classList.add('is-visible');
      }
    }
  }

  /**
   * Initialize event listeners and observers
   */
  init() {
    // Hydrate deferred promo images the first time the drawer opens (before it becomes visible)
    this.menuDrawer.addEventListener('dialog:before-show', () => this.hydratePromoImages(), { once: true });

    // Listen for clicks on panel toggle buttons
    this.menuDrawer.addEventListener('click', (e) => this.handlePanelToggle(e));

    // Observe main panel to prevent it from being hidden in desktop
    const mainPanelObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          this.preventMainPanelHide();
        }
      });
    });

    mainPanelObserver.observe(this.mainPanel, {
      attributes: true,
      attributeFilter: ['class']
    });

    // Observe all secondary and tertiary panels for button state updates
    const panelObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          this.updateButtonState(mutation.target);
        }
      });
    });

    const panels = this.menuDrawer.querySelectorAll('header-menu-drawer-panel:not(#menu-drawer-panel-main)');
    panels.forEach((panel) => {
      panelObserver.observe(panel, {
        attributes: true,
        attributeFilter: ['class']
      });
    });

    // Ensure main panel is visible on window resize to desktop
    window.addEventListener('resize', () => this.ensureMainPanelVisible());
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  new MegaMenuController('menu-drawer');
});
